export interface CaptionPresetConfig {
  fontFamily: string
  fontSize: number
  fontWeight: number
  color: string
  background: string
  highlightColor: string
  position: string
  animation: string
  shadow: string
  stroke?: string
  strokeWidth?: number
  letterSpacing?: string
  wordAnimation?: string
  glow?: string
}

/**
 * Caption styles — the single source of truth for both the editor's Caption
 * Style panel and the agent's `generate_captions` tool. Each baked onto every
 * word-timed overlay at creation; the per-word motion (`wordAnimation`) is
 * eased in computeWordStyle (see TextOverlayRenderer).
 *
 * Order matters: it's the order shown in the picker. Default first, then the
 * dynamic / bold YouTube · TikTok styles, then the refined/calm ones.
 *
 * wordAnimation vocabulary:
 *   pop      — gentle eased scale + highlight bloom (the nuanced default)
 *   punch    — snappy overshoot, bigger scale (YouTube)
 *   bounce   — playful springy pop-in from small (TikTok)
 *   box      — active word sits in a bright highlight box (Hormozi)
 *   spotlight— inactive dimmed, active blooms bright
 *   cascade  — stacked words, active lifts
 *   fill     — stroke-only outline that fills on active
 *   typewriter — words appear as spoken
 *   solo     — one word at a time, gentle ease
 *   solo_pop — one word at a time, punchy overshoot (Word Pop / Slam)
 *   none     — static subtitles, no per-word emphasis
 */
export const DEFAULT_CAPTION_PRESET = 'bold'

export const CAPTION_PRESETS: Record<string, CaptionPresetConfig> = {
  // ── DEFAULT — polished & nuanced ────────────────────────────────────────
  // Heavy geometric sans on a soft rounded scrim; a gentle eased word-pop with
  // a warm highlight bloom. Reads premium and legible, not garish.
  bold: {
    fontFamily: 'Outfit',
    fontSize: 52,
    fontWeight: 800,
    color: 'rgba(255,255,255,0.9)',
    background: 'rgba(8,10,18,0.5)',
    highlightColor: '#ffd84d',
    position: 'bottom_center',
    animation: 'none',
    shadow: '0 2px 10px rgba(0,0,0,0.55), 0 1px 2px rgba(0,0,0,0.6)',
    wordAnimation: 'pop',
    letterSpacing: '-0.005em',
  },

  // ── Dynamic / bold (YouTube · TikTok) ───────────────────────────────────
  // MrBeast / YouTube: heavy sans, thick black outline + chunky shadow, a snappy
  // overshoot punch on each word, bright yellow highlight.
  youtube: {
    fontFamily: 'Montserrat',
    fontSize: 60,
    fontWeight: 800,
    color: '#ffffff',
    background: 'transparent',
    highlightColor: '#ffe14d',
    position: 'bottom_center',
    animation: 'none',
    shadow: '0 3px 0 rgba(0,0,0,0.5), 0 6px 18px rgba(0,0,0,0.55)',
    stroke: '#000000',
    strokeWidth: 3,
    wordAnimation: 'punch',
    letterSpacing: '0',
  },
  // TikTok: towering condensed display, thick outline, springy bounce-in.
  tiktok_pop: {
    fontFamily: 'Bebas Neue',
    fontSize: 84,
    fontWeight: 400,
    color: '#ffffff',
    background: 'transparent',
    highlightColor: '#ff2d55',
    position: 'center',
    animation: 'none',
    shadow: '0 3px 0 rgba(0,0,0,0.45), 0 8px 22px rgba(0,0,0,0.4)',
    stroke: '#000000',
    strokeWidth: 3,
    wordAnimation: 'bounce',
  },
  // Hormozi: bold caps energy — the active word sits in a bright green box.
  hormozi: {
    fontFamily: 'Montserrat',
    fontSize: 58,
    fontWeight: 800,
    color: '#ffffff',
    background: 'transparent',
    highlightColor: '#00e676',
    position: 'center',
    animation: 'none',
    shadow: '0 3px 0 rgba(0,0,0,0.5), 0 6px 18px rgba(0,0,0,0.5)',
    stroke: '#000000',
    strokeWidth: 3,
    wordAnimation: 'box',
    letterSpacing: '-0.01em',
  },
  // Spotlight: inactive dimmed, active blooms bright with an eased scale.
  spotlight: {
    fontFamily: 'Barlow Condensed',
    fontSize: 58,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.22)',
    background: 'transparent',
    highlightColor: '#ffffff',
    position: 'bottom_center',
    animation: 'none',
    shadow: '0 2px 12px rgba(0,0,0,0.75)',
    wordAnimation: 'spotlight',
    letterSpacing: '0.01em',
  },
  // Accent: soft inactive words, a vivid violet bloom on the active one.
  colorful: {
    fontFamily: 'Outfit',
    fontSize: 50,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.45)',
    background: 'rgba(8,10,18,0.45)',
    highlightColor: '#a78bfa',
    position: 'bottom_center',
    animation: 'none',
    shadow: '0 2px 8px rgba(0,0,0,0.6)',
    wordAnimation: 'pop',
  },
  // Cascade: stacked words, the active one lifts and warms.
  cascade: {
    fontFamily: 'Outfit',
    fontSize: 52,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.55)',
    background: 'transparent',
    highlightColor: '#ff8f6b',
    position: 'center',
    animation: 'none',
    shadow: '0 4px 14px rgba(0,0,0,0.9), 0 0 2px rgba(0,0,0,0.5)',
    wordAnimation: 'cascade',
    letterSpacing: '0.02em',
  },
  // Glow: soft neon bloom that intensifies on the active word.
  neon: {
    fontFamily: 'Outfit',
    fontSize: 48,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.6)',
    background: 'transparent',
    highlightColor: '#c4b5fd',
    position: 'bottom_center',
    animation: 'none',
    shadow: '0 0 6px rgba(196,181,253,0.3)',
    glow: '0 0 8px rgba(196,181,253,0.45), 0 0 26px rgba(196,181,253,0.2)',
    wordAnimation: 'pop',
  },
  // Outline: stroke-only words that fill in as spoken.
  outline: {
    fontFamily: 'Montserrat',
    fontSize: 54,
    fontWeight: 700,
    color: 'transparent',
    background: 'transparent',
    highlightColor: '#ffffff',
    position: 'bottom_center',
    animation: 'none',
    shadow: '0 2px 10px rgba(0,0,0,0.5)',
    stroke: '#ffffff',
    strokeWidth: 2,
    wordAnimation: 'fill',
  },
  // ── One word at a time ──────────────────────────────────────────────────
  // Solo: one clean word at a time, centered, eased in and out.
  solo: {
    fontFamily: 'Montserrat',
    fontSize: 72,
    fontWeight: 800,
    color: '#ffffff',
    background: 'transparent',
    highlightColor: '#ffffff',
    position: 'center',
    animation: 'none',
    shadow: '0 4px 22px rgba(0,0,0,0.85)',
    wordAnimation: 'solo',
    letterSpacing: '-0.015em',
  },
  // Word Pop: one word at a time, snappy overshoot, vivid highlight color.
  word_pop: {
    fontFamily: 'Outfit',
    fontSize: 78,
    fontWeight: 800,
    color: '#ffffff',
    background: 'transparent',
    highlightColor: '#ffd84d',
    position: 'center',
    animation: 'none',
    shadow: '0 4px 18px rgba(0,0,0,0.7)',
    wordAnimation: 'solo_pop',
    letterSpacing: '-0.02em',
  },
  // Slam: one towering outlined word at a time, punched in. TikTok energy.
  word_slam: {
    fontFamily: 'Bebas Neue',
    fontSize: 124,
    fontWeight: 400,
    color: '#ffffff',
    background: 'transparent',
    highlightColor: '#ffffff',
    position: 'center',
    animation: 'none',
    shadow: '0 4px 0 rgba(0,0,0,0.4), 0 12px 34px rgba(0,0,0,0.5)',
    stroke: '#000000',
    strokeWidth: 3,
    wordAnimation: 'solo_pop',
    letterSpacing: '0.01em',
  },
  // ── Refined / calm ──────────────────────────────────────────────────────
  // Minimal: small, clean, a whisper of pop on the active word.
  minimal: {
    fontFamily: 'Inter',
    fontSize: 36,
    fontWeight: 500,
    color: 'rgba(255,255,255,0.82)',
    background: 'rgba(8,10,18,0.32)',
    highlightColor: '#ffffff',
    position: 'bottom_center',
    animation: 'none',
    shadow: '0 1px 5px rgba(0,0,0,0.5)',
    wordAnimation: 'pop',
    letterSpacing: '0.005em',
  },
  // Cinema: warm film-subtitle bar, no per-word emphasis.
  cinema: {
    fontFamily: 'Inter',
    fontSize: 33,
    fontWeight: 400,
    color: '#f3ecd9',
    background: 'rgba(0,0,0,0.88)',
    highlightColor: '#ffffff',
    position: 'bottom_center',
    animation: 'none',
    shadow: 'none',
    wordAnimation: 'none',
    letterSpacing: '0.02em',
  },
  // Elegant: serif subtitles on a dark bar.
  elegant: {
    fontFamily: 'Libre Bodoni',
    fontSize: 37,
    fontWeight: 400,
    color: '#f5f0e8',
    background: 'rgba(0,0,0,0.8)',
    highlightColor: '#ffffff',
    position: 'bottom_center',
    animation: 'none',
    shadow: 'none',
    wordAnimation: 'none',
    letterSpacing: '0.03em',
  },
  // Typewriter: monospace, words appear as spoken.
  typewriter: {
    fontFamily: 'JetBrains Mono',
    fontSize: 35,
    fontWeight: 500,
    color: '#e8dcc8',
    background: 'rgba(0,0,0,0.8)',
    highlightColor: '#ffffff',
    position: 'bottom_center',
    animation: 'none',
    shadow: 'none',
    wordAnimation: 'typewriter',
    letterSpacing: '0.01em',
  },
  // Broadcast: condensed news lower-third, subtle pop.
  broadcast: {
    fontFamily: 'Barlow Condensed',
    fontSize: 44,
    fontWeight: 700,
    color: '#ffffff',
    background: 'rgba(8,10,18,0.82)',
    highlightColor: '#ffd84d',
    position: 'bottom_center',
    animation: 'none',
    shadow: '0 2px 6px rgba(0,0,0,0.6)',
    wordAnimation: 'pop',
    letterSpacing: '0.03em',
  },
}

export const CAPTION_PRESET_NAMES = Object.keys(CAPTION_PRESETS) as (keyof typeof CAPTION_PRESETS)[]

export const CAPTION_PRESET_LABELS: Record<string, string> = {
  bold: 'Bold',
  youtube: 'YouTube',
  tiktok_pop: 'TikTok',
  hormozi: 'Hormozi',
  spotlight: 'Spotlight',
  colorful: 'Accent',
  cascade: 'Cascade',
  neon: 'Glow',
  outline: 'Outline',
  solo: 'Solo',
  word_pop: 'Word Pop',
  word_slam: 'Slam',
  minimal: 'Minimal',
  cinema: 'Cinema',
  elegant: 'Elegant',
  typewriter: 'Typewriter',
  broadcast: 'Broadcast',
}

export const CAPTION_PRESET_DESCRIPTIONS: Record<string, string> = {
  bold: 'Clean heavy sans, soft scrim, gentle word pop',
  youtube: 'Thick outline, punchy yellow highlight',
  tiktok_pop: 'Towering display, springy bounce per word',
  hormozi: 'Active word in a bright highlight box',
  spotlight: 'Dim inactive, bright active word',
  colorful: 'Soft words, vivid accent on active',
  cascade: 'Stacked words, active lifts',
  neon: 'Soft glow that intensifies on active',
  outline: 'Stroke-only text, fills as spoken',
  solo: 'One word at a time, clean and centered',
  word_pop: 'One word at a time, snappy color pop',
  word_slam: 'One word at a time, towering outlined slam',
  minimal: 'Small, clean, understated',
  cinema: 'Warm film-subtitle bar',
  elegant: 'Serif subtitles on a dark bar',
  typewriter: 'Monospace, words appear as spoken',
  broadcast: 'Condensed news-style captions',
}
